import React, { useEffect, useMemo, useState } from 'react';
import { MRT_ColumnDef, useMaterialReactTable, MRT_Row, MaterialReactTable } from 'material-react-table';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PageviewIcon from '@mui/icons-material/Pageview';
import { FormField } from '../interfaces/User.interface';
import { Role } from '../interfaces/constants';
import { useSetting } from '../contexts/SettingContext';
import FDynamicForm from '../utils/FormBuilder/FDynamicForm';
import DialogWithClose from './DialogWithClose';

interface DynamicTableProps<T> {
    tableName?: string;
    formFields: FormField[];
    data: T[];
    handlers: {
        fetchSettings: () => void;
        handleCreate?: (data: any) => Promise<any>;
        handleEdit?: (data: any) => Promise<any>;
        handleDelete?: (data: any) => Promise<any>;
        handleView?: (data: any) => Promise<any>;
    };
    allowEdit?: boolean;
    allowDelete?: boolean;
    allowView?: boolean;
    complexTable?: boolean;
    prefilled?: Partial<T>;
    size?: number;

    onFormChange?: (fieldName: string, value: any) => void;

    //列表点击弹窗
    onRowClick?: (row: T) => void;
    RowDialogComponent?: React.FC<{ data: T | null; open: boolean; onClose: () => void }>;
}
    
const DynamicTable = <T extends { [key: string]: any }>({ tableName, formFields, prefilled, data, handlers, complexTable, size, onFormChange, allowEdit = true, allowDelete = true, allowView = true, onRowClick, RowDialogComponent}: DynamicTableProps<T>) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentRow, setCurrentRow] = useState<T | null>(null);
    const [tableData, setTableData] = useState<T[]>(data);
    const { settings } = useSetting();
    const [rowClickData, setRowClickData] = useState<T | null>(null); 
    const [openRowDialog, setOpenRowDialog] = useState(false);  

    const handleCreate = handlers.handleCreate;
    useEffect(() => {
        // reverse the data
        setTableData(data);
    }, [data]);

    useEffect(() => {
        if (isEditing && currentRow?.type) {
          onFormChange?.('type', currentRow.type);
        }
      }, [isEditing, currentRow]);

    const columns: Array<MRT_ColumnDef<T>> = useMemo(() => {
        const actionColumn: MRT_ColumnDef<T> = {
            header: 'Actions',
            id: 'actions',
            Cell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                    {allowEdit && (
                    <Tooltip title="Edit">
                        <IconButton onClick={(e) => {
                            e.stopPropagation();
                            setCurrentRow(row.original);
                            setIsEditing(true);
                        }}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    )}
                    {allowDelete && (
                    <Tooltip title="Delete">
                        <IconButton color="error" onClick={(e) => {
                            e.stopPropagation();
                            setCurrentRow(row.original);
                            setIsDeleting(true);
                        }}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                    )}
                    {allowView && (
                    <Tooltip title="View">
                        <IconButton onClick={(e) => {
                            e.stopPropagation();
                            handlers.handleView!(row.original);
                        }}>
                            <PageviewIcon />
                        </IconButton>
                    </Tooltip>
                    )}
                </Box>
            ),
            size: 150,
            minSize: 120,
            maxSize: 200,
        };

        const dynamicColumns = formFields.map((row): MRT_ColumnDef<T> => {
            if (row.displayFn) {
              return {
                header: row.question,
                accessorKey: row.name,
                Cell: ({ cell }: { cell: any }) => row.displayFn!(cell.getValue()),
              };
            }
            return {
              header: row.question,
              accessorKey: row.name,
            };
          });
        if(allowView || allowEdit || allowDelete) {
            return [actionColumn, ...dynamicColumns];
        }
        return dynamicColumns;
    }, [formFields]);

    const table = useMaterialReactTable({
        columns,
        initialState: { pagination: { pageIndex: 0, pageSize: size ?? 10 } },
        data: tableData,
        enableColumnActions: true,
        enableColumnFilters: true,
        enablePagination: true,
        enableSorting: false,
        enableTopToolbar: true,
        enableToolbarInternalActions: complexTable,

        muiTableBodyRowProps: ({ row }) => ({
            onClick: () => {
              onRowClick?.(row.original);
              setRowClickData(row.original); 
              setOpenRowDialog(true);   
            },
        }),

        renderTopToolbarCustomActions: () => (
             (handleCreate && settings.profile.role === Role.Admin) &&
            <Button variant="contained" onClick={() => setIsCreating(true)}>
                {tableName? `Create New ${tableName}`:"Create New Entry"}
            </Button>
            
        ),
        muiTableBodyCellProps: () => {
            // set font to 18
            return {
              sx: {
                fontSize: 16,
              },
            }
          },
        muiTableHeadCellProps: () => {
            // set font to 18
            return {
              sx: {
                fontSize: 18,
              },
            }
        },       
    });

    const handleFormSubmit = (data: any) => {
        if (isCreating) {
            handlers.handleCreate!(data).finally(() => {
                handlers.fetchSettings();
                setIsCreating(false);
            });
        } else if (isEditing && currentRow) {
            handlers.handleEdit?.({ ...currentRow, ...data }).finally(() => {
                handlers.fetchSettings();
                setIsEditing(false);
                setCurrentRow(null);
            });
        }
    };

    const handleDelete = () => {
        if (currentRow) {
            handlers.handleDelete?.(currentRow).finally(() => {
                handlers.fetchSettings();
                setIsDeleting(false);
                setCurrentRow(null);
            });
        }
    };
    
    return (
        <Box>
            {tableName && (
                <Typography variant="h5" sx={{ mb: 2 }}>
                    {tableName}
                </Typography>
            )}
            <MaterialReactTable table={table} />
            <DialogWithClose fullWidth={true} maxWidth={'md'} open={isCreating} onClose={() => setIsCreating(false)}>
                <DialogTitle>Create Entry</DialogTitle>
                <DialogContent>
                    <FDynamicForm formFields={formFields} prefilled={prefilled} onSubmit={handleFormSubmit} onFieldChange={onFormChange}/>
                </DialogContent>
            </DialogWithClose>
            <DialogWithClose fullWidth={true} maxWidth={'md'} open={isEditing} onClose={() => setIsEditing(false)}>
                <DialogTitle>Edit Entry</DialogTitle>
                <DialogContent>
                    <FDynamicForm formFields={formFields} prefilled={currentRow || {}} onSubmit={handleFormSubmit} onFieldChange={onFormChange}/>
                </DialogContent>
            </DialogWithClose>
            <DialogWithClose fullWidth={true} maxWidth={'sm'} open={isDeleting} onClose={() => setIsDeleting(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
            
                <DialogContent>
                    <Typography>Are you sure you want to delete this entry?</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button variant="contained" color="secondary" onClick={() => setIsDeleting(false)}>
                            Cancel
                        </Button>
                        <Button variant="contained" color="error" onClick={handleDelete} sx={{ ml: 2 }}>
                            Delete
                        </Button>
                    </Box>
                </DialogContent>
            </DialogWithClose>
            {RowDialogComponent && (
                <RowDialogComponent
                data={rowClickData}
                open={openRowDialog}
                onClose={() => setOpenRowDialog(false)}
                />
            )}
        </Box>
    )
};

export default DynamicTable;
